import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Info } from "lucide-react";
import { BasePatch, DocumentBase } from "../types/base";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shared/components/ui/tabs";
import { InputField } from "@/shared/components/field/input-field";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { Card, CardContent } from "@/shared/components/ui/card";
import { AddressFieldsDebounced } from "../../addresses/components/address-fields-debounced";
import { ServiceObjectsSelect } from "../../customers/components/service-objects-select";
import { ServiceObjectReferencesBaseCard } from "./service-object-references-base-card";
import { updateServiceObjectsByIds } from "../types/service-object-references-base";
import { Field } from "@/shared/components/ui/field";
import { BranchReferencesBaseSelect } from "./branch-references-base-select";
import { BranchReferencesBaseCard } from "./branch-references-base-card";

/**
 * DocumentBaseDialog shows all the DocumentBase data associated
 * with the document. Some data is editable, since this may be
 * needed by users in edge cases.
 * Most data should be edited through the default UI of the document though.
 */
export function DocumentBaseDialog({
  document,
  onPatch,
  disabled,
}: {
  document: DocumentBase;
  onPatch: (p: BasePatch) => void;
  disabled?: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Info />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-max">
        <DialogHeader>
          <DialogTitle>
            {t(`Dokument Basisdaten für ${document.referenceNumber}`)}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Die Basisdaten welche mit dem Dokument verknüpft sind. Änderungen beziehen sich nur auf das aktuelle Dokument und nicht auf die im System hinterlegten Daten.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Tabs>
          <TabsList>
            <TabsTrigger value="general">{t("Allgemein")}</TabsTrigger>
            <TabsTrigger value="company">{t("Unternehmen")}</TabsTrigger>
            <TabsTrigger value="branches">{t("Niederlassungen")}</TabsTrigger>
            <TabsTrigger value="customer">{t("Kunde")}</TabsTrigger>
            <TabsTrigger value="serviceObjects">
              {t("Serviceobjekte")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="general">
            <GeneralTab document={document} />
          </TabsContent>
          <TabsContent value="company">
            <CompanyTab
              document={document}
              onPatch={onPatch}
              disabled={disabled}
            />
          </TabsContent>
          <TabsContent value="branches">
            <BranchesTab
              document={document}
              onPatch={onPatch}
              disabled={disabled}
            />
          </TabsContent>
          <TabsContent value="customer">
            <CustomerTab
              document={document}
              onPatch={onPatch}
              disabled={disabled}
            />
          </TabsContent>
          <TabsContent value="serviceObjects">
            <ServiceObjectsTab
              document={document}
              onPatch={onPatch}
              disabled={disabled}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function GeneralTab({ document }: { document: DocumentBase }) {
  return (
    <Card>
      <CardContent>
        <div className="grid gap-4 lg:grid-cols-3">
          <InputField
            label={t("ID")}
            value={document.id}
            // display full UUID
            className="min-w-[320px]"
            disabled
          />
          <InputField
            label={t("Dokumentenart")}
            value={t(document.documentKind)}
            disabled
          />
          <InputField
            label={t("Nummber")}
            value={t(document.referenceNumber)}
            disabled
          />
          <InputField
            label={t("Vorgang ID")}
            value={document.processId ?? undefined}
            // display full UUID
            className="min-w-[320px]"
            disabled
          />
          <DatePickerField
            label={t("Erstellt am")}
            date={document.createdAt}
            onDateChange={() => null}
            nullable={false}
            disabled
          />
          <DatePickerField
            label={t("Aktualisiert am")}
            date={document.updatedAt}
            onDateChange={() => null}
            nullable={false}
            disabled
          />
          <InputField label={t("Status")} value={t(document.state)} disabled />
          <DatePickerField
            label={t("Status aktualisiert am")}
            date={document.stateSetAt}
            onDateChange={() => null}
            nullable
            disabled
          />
          <InputField
            label={t("Status gesetzt durch")}
            value={document.stateSetBy}
            disabled
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CompanyTab({
  document,
  onPatch,
  disabled,
}: {
  document: DocumentBase;
  onPatch: (p: BasePatch) => void;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardContent>
        <div className="grid gap-4">
          <InputField
            label={t("Unternehmen ID")}
            value={document.companyId}
            // display full UUID
            className="min-w-[320px]"
            disabled
          />
          <AddressFieldsDebounced
            address={document.companyAddress}
            onAddressChange={(companyAddress) => onPatch({ companyAddress })}
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function BranchesTab({
  document,
  onPatch,
  disabled,
}: {
  document: DocumentBase;
  onPatch: (p: BasePatch) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-2">
      <Field>
        <BranchReferencesBaseSelect
          value={document}
          onValueChange={(references) => onPatch(references)}
          companyId={document.companyId}
          disabled={disabled}
        />
      </Field>
      <BranchReferencesBaseCard
        references={document}
        onReferencesChange={(references) => onPatch(references)}
        disabled={disabled}
      />
    </div>
  );
}

function CustomerTab({
  document,
  onPatch,
  disabled,
}: {
  document: DocumentBase;
  onPatch: (p: BasePatch) => void;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardContent>
        <div className="grid gap-4">
          <InputField
            label={t("Kunde ID")}
            value={document.customerId}
            // display full UUID
            className="min-w-[320px]"
            disabled
          />
          <AddressFieldsDebounced
            address={document.customerAddress}
            onAddressChange={(customerAddress) => onPatch({ customerAddress })}
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ServiceObjectsTab({
  document,
  onPatch,
  disabled,
}: {
  document: DocumentBase;
  onPatch: (p: BasePatch) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-2">
      <Field>
        <ServiceObjectsSelect
          customerId={document.customerId}
          value={document.serviceObjects.map((s) => s.serviceObjectId)}
          onValueChange={(serviceObjectIds) => {
            onPatch(updateServiceObjectsByIds(document, serviceObjectIds));
          }}
          disabled={disabled}
        />
      </Field>
      <ServiceObjectReferencesBaseCard
        references={document}
        onReferencesChange={(references) => onPatch(references)}
        disabled={disabled}
      />
    </div>
  );
}
